/**
 * Capacitor native implementation of the Platform abstraction.
 *
 * Wires each method to the appropriate @capacitor/* plugin per ADR-063 § 3.
 * Secure storage uses @capacitor/preferences (Android Keystore-backed),
 * satisfying docs/security-rules.md — never localStorage on native.
 *
 * Camera.getPhoto / CameraResultType / CameraSource are deprecated in
 * @capacitor/camera v8 but still functional. Migration to the split
 * takePhoto / chooseFromGallery API is deliberately deferred to a follow-up
 * PR (Relates to #21709) — this PR is a mechanical version bump only.
 */

import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Geolocation } from '@capacitor/geolocation';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Network } from '@capacitor/network';
import { Preferences } from '@capacitor/preferences';
import { Clipboard } from '@capacitor/clipboard';
import { Share } from '@capacitor/share';
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';
import { FileOpener } from '@capacitor-community/file-opener';

import type {
  GeoPosition,
  NetworkStatus,
  NotifyPayload,
  PhotoCapture,
  Platform,
  RealtimeConnection,
  RealtimeHandlers,
  SaveToDownloadsResult,
  SharePayload,
} from './types';

/** Convert a Blob to a base64 string (no data-URL prefix). */
async function blob_to_base64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function is_record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * A local-notification id. The Android plugin parses `id` as a Java `int`,
 * so `Date.now()` (~1.7e12) overflows it — keep it inside int32.
 */
function notification_id(): number {
  return Math.floor(Math.random() * 2_147_483_647);
}

export const capacitorPlatform: Platform = {
  /**
   * Write the file to the device, then hand it straight to the OS share sheet.
   *
   * The write alone is not a usable "download" on a phone. `Filesystem.writeFile`
   * is a plain file write: unlike a browser transfer it never goes through
   * Android's `DownloadManager`, so it raises NO download notification and
   * creates NO entry in Downloads. The file lands in the public Documents
   * folder (`Directory.Documents` -> `Environment.DIRECTORY_DOCUMENTS`) and
   * MediaScanner indexes it there, which means the only way a user could find
   * an export was to open a file manager and go looking for it. Every export in
   * WagePro (employee directory, payroll import template, statutory reports,
   * payslip PDFs, the bank disbursement file) was silently correct and
   * effectively undiscoverable — reported on a real device, 2026-08-27.
   *
   * Handing the written file to `Share.share` is the platform-idiomatic
   * equivalent of the browser's download shelf: the user gets an immediate,
   * actionable sheet (open in Sheets/Drive, mail it, save elsewhere) at the
   * moment the export happens, instead of a silent no-op. The file is still
   * persisted to Documents first, so dismissing the sheet loses nothing.
   *
   * Sharing from Documents needs no native change: the shell's FileProvider
   * already declares `<external-path path="."/>`
   * (`apps/wagepro-mobile/android/app/src/main/res/xml/file_paths.xml`), which
   * covers everything under `/storage/emulated/0`, Documents included.
   *
   * The share leg is deliberately non-fatal. A dismissed sheet rejects, and so
   * does a device with no share target — neither is a download failure, because
   * the write has already succeeded. A rejection here must not propagate and
   * make a successful export look broken; a genuine `writeFile` failure still
   * rejects, so callers can surface it.
   */
  async download(blob: Blob, filename: string): Promise<void> {
    const data = await blob_to_base64(blob);
    const written = await Filesystem.writeFile({
      path: filename,
      data,
      directory: Directory.Documents,
    });
    try {
      await Share.share({ title: filename, files: [written.uri] });
    } catch {
      // Sheet dismissed, or nothing on the device can receive the file. The
      // export itself succeeded — see this method's docblock.
    }
  },

  /**
   * Save into the phone's public Downloads, with no share sheet — the caller
   * raises a local notification that opens the file on tap.
   *
   * Android: `ExternalStorage` + `Download/<name>` lands in
   * `/storage/emulated/0/Download`. On API 30+ an app may create its own
   * files in that collection with no storage permission; API 29 relies on
   * `requestLegacyExternalStorage` and API <= 28 on the runtime
   * WRITE_EXTERNAL_STORAGE prompt the Filesystem plugin raises itself (both
   * declared in the shell's AndroidManifest). If that write still fails, the
   * file goes to public Documents — where `download` has always put it — so
   * the save never fails just because Downloads was unavailable.
   *
   * iOS has no public Downloads: `ExternalStorage` aliases the app's
   * Documents folder, which the shell exposes in the Files app.
   */
  async saveToDownloads(
    blob: Blob,
    filename: string,
  ): Promise<SaveToDownloadsResult> {
    const data = await blob_to_base64(blob);
    try {
      const written = await Filesystem.writeFile({
        path: `Download/${filename}`,
        data,
        directory: Directory.ExternalStorage,
        recursive: true,
      });
      return { uri: written.uri };
    } catch (err) {
      console.error(
        '[platform] saving to Download failed; falling back to Documents',
        err instanceof Error ? err.message : 'unknown error',
      );
      const written = await Filesystem.writeFile({
        path: filename,
        data,
        directory: Directory.Documents,
      });
      return { uri: written.uri };
    }
  },

  /**
   * Open a saved file in the device's default viewer. With no viewer for the
   * type installed (FILE_NOT_SUPPORTED), offer the share sheet instead so the
   * user can still get at the file.
   */
  async openFile(uri: string, mimeType: string): Promise<boolean> {
    try {
      await FileOpener.open({
        filePath: uri,
        contentType: mimeType,
        openWithDefault: true,
      });
      return true;
    } catch (err) {
      console.error(
        '[platform] opening file failed; offering the share sheet',
        err instanceof Error ? err.message : 'unknown error',
      );
      try {
        await Share.share({ files: [uri] });
      } catch (share_err) {
        // Sheet dismissed or nothing can receive the file — not a failure
        // of the open itself, but logged so it is never silent.
        console.warn(
          '[platform] share sheet for the unopened file was dismissed',
          share_err instanceof Error ? share_err.message : 'unknown error',
        );
      }
      return false;
    }
  },

  onNotificationTap(listener): () => void {
    const handle = LocalNotifications.addListener(
      'localNotificationActionPerformed',
      (action) => {
        // The plugin types `extra` as `any`; narrow it before use.
        const extra: unknown = action.notification.extra;
        const href =
          is_record(extra) && typeof extra.href === 'string'
            ? extra.href
            : undefined;
        const mimeType =
          is_record(extra) && typeof extra.mimeType === 'string'
            ? extra.mimeType
            : undefined;
        listener({
          title: action.notification.title,
          body: action.notification.body,
          ...(href !== undefined ? { href } : {}),
          ...(mimeType !== undefined ? { mimeType } : {}),
        });
      },
    );
    return () => {
      void handle.then((h) => h.remove());
    };
  },

  async share(payload: SharePayload): Promise<boolean> {
    try {
      await Share.share({
        title: payload.title,
        text: payload.text,
        url: payload.url,
      });
      return true;
    } catch {
      // User cancel or share sheet dismissed — not a real error.
      return false;
    }
  },

  clipboard: {
    async write(text: string): Promise<void> {
      await Clipboard.write({ string: text });
    },
    async read(): Promise<string> {
      const result = await Clipboard.read();
      return result.value;
    },
  },

  async notify(payload: NotifyPayload): Promise<boolean> {
    let status = await LocalNotifications.checkPermissions();
    if (
      status.display === 'prompt' ||
      status.display === 'prompt-with-rationale'
    ) {
      status = await LocalNotifications.requestPermissions();
    }
    // Denied (Android 13+ POST_NOTIFICATIONS, or iOS) — nothing to show.
    if (status.display !== 'granted') return false;
    await LocalNotifications.schedule({
      notifications: [
        {
          title: payload.title,
          body: payload.body,
          id: notification_id(),
          ...(payload.href !== undefined
            ? {
                extra: {
                  href: payload.href,
                  ...(payload.mimeType !== undefined
                    ? { mimeType: payload.mimeType }
                    : {}),
                },
              }
            : {}),
        },
      ],
    });
    return true;
  },

  vibrate(pattern: 'tap' | 'success' | 'warning' | 'error'): void {
    if (pattern === 'tap') {
      void Haptics.impact({ style: ImpactStyle.Light });
    } else if (pattern === 'success') {
      void Haptics.notification({ type: NotificationType.Success });
    } else if (pattern === 'warning') {
      void Haptics.notification({ type: NotificationType.Warning });
    } else {
      void Haptics.notification({ type: NotificationType.Error });
    }
  },

  secureStorage: {
    async set(key: string, value: string): Promise<void> {
      await Preferences.set({ key, value });
    },
    async get(key: string): Promise<string | null> {
      const { value } = await Preferences.get({ key });
      return value;
    },
    async remove(key: string): Promise<void> {
      await Preferences.remove({ key });
    },
  },

  isNative: () => true,

  camera: {
    async capturePhoto(): Promise<PhotoCapture> {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        quality: 80,
      });
      const response = await fetch(photo.webPath ?? '');
      const blob = await response.blob();
      return { blob, mimeType: `image/${photo.format}` };
    },
    isAvailable(): boolean {
      return true;
    },
  },

  geolocation: {
    async getCurrentPosition(): Promise<GeoPosition> {
      const pos = await Geolocation.getCurrentPosition();
      return {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        timestamp: pos.timestamp,
      };
    },
    isAvailable(): boolean {
      return true;
    },
  },

  realtime: {
    connect(url: string, handlers: RealtimeHandlers): RealtimeConnection {
      const ws = new WebSocket(url);
      ws.onmessage = (ev: MessageEvent) => {
        handlers.onMessage(ev.data as string);
      };
      if (handlers.onOpen)
        ws.onopen = () => {
          handlers.onOpen?.();
        };
      if (handlers.onClose)
        ws.onclose = () => {
          handlers.onClose?.();
        };
      if (handlers.onError)
        ws.onerror = (ev) => {
          handlers.onError?.(ev);
        };
      return {
        send: (data: string) => {
          if (ws.readyState === WebSocket.OPEN) ws.send(data);
        },
        close: () => {
          ws.close();
        },
      };
    },
  },

  network: {
    async getStatus(): Promise<NetworkStatus> {
      const status = await Network.getStatus();
      return status.connected ? 'online' : 'offline';
    },
    onStatusChange(listener: (status: NetworkStatus) => void): () => void {
      const handle_promise = Network.addListener(
        'networkStatusChange',
        (status) => {
          listener(status.connected ? 'online' : 'offline');
        },
      );
      return () => {
        void handle_promise.then((handle: { remove: () => unknown }) =>
          handle.remove(),
        );
      };
    },
  },

  biometrics: {
    async isAvailable(): Promise<boolean> {
      const result = await BiometricAuth.checkBiometry();
      return result.isAvailable;
    },
    async authenticate(reason: string): Promise<boolean> {
      try {
        await BiometricAuth.authenticate({ reason });
        return true;
      } catch {
        // User cancel or biometric failure.
        return false;
      }
    },
  },
};
